import { Button } from "@/component/Button";
import { unseenVersionStore } from "@/store/unseenVersion";
import { LoadingOutlined } from "@ant-design/icons";
import { useWavesurfer } from "@wavesurfer/react";
import classNames from "classnames";
import { observer } from "mobx-react-lite";
import React, { memo, useMemo, useRef } from "react";
import styles from "../../index.module.css";
import { PositionedItem, buildTrackLayout } from "../../logic/timelineLayout";
import { TimelineTrack } from "../../types";
import {
  TIMELINE_PADDING,
  TRACK_ICON_WIDTH,
  getTrackTheme,
} from "./trackTheme";

type TrackRowProps = {
  index: number;
  track: TimelineTrack;
  trackWidth: number;
  pixelsPerMs: number;
  onSelectItem?: (itemId: string) => void;
};

function TrackRowComponent({
  index,
  track,
  trackWidth,
  pixelsPerMs,
  onSelectItem,
}: TrackRowProps) {
  const items = useMemo(() => buildTrackLayout(track), [track]);
  const theme = useMemo(() => getTrackTheme(track.kind), [track.kind]);
  const height = useMemo(() => {
    if (track.items.length === 0) {
      return theme?.height ?? 0;
    }
    return undefined;
  }, [track, theme]);

  const { Icon } = theme;

  const contentWidth = trackWidth + TRACK_ICON_WIDTH + TIMELINE_PADDING;

  return (
    <div className="relative min-w-full" style={{ width: contentWidth }}>
      {index > 0 && (
        <div
          className="absolute top-0 left-0 min-w-full z-20 h-px bg-text-primary/[.08]"
          style={{ width: contentWidth }}
        />
      )}
      <div
        className="relative flex pr-10 h-full items-stretch"
        role="presentation"
        style={{ height }}
      >
        <div
          className="sticky left-0 mr-1 shrink-0 z-30 flex items-center justify-center bg-background-elevated-2"
          style={{ width: TRACK_ICON_WIDTH }}
        >
          {Icon ? <Icon className="w-4 h-4 text-text-primary" /> : null}
        </div>
        <SequentialItems
          trackId={track.id}
          items={items}
          onSelectItem={onSelectItem}
          pixelsPerMs={pixelsPerMs}
        />
      </div>
    </div>
  );
}

type SequentialItemsProps = {
  trackId: string;
  items: PositionedItem<TimelineTrack["items"][number]>[];
  pixelsPerMs: number;
  onSelectItem?: (itemId: string) => void;
};

function SequentialItemsComponent({
  trackId,
  items,
  onSelectItem,
  pixelsPerMs,
}: SequentialItemsProps) {
  if (!items.length) {
    return null;
  }

  const blocks: React.ReactNode[] = [];
  let cursor = 0;

  items.forEach((layout, index) => {
    const gap = layout.start - cursor;
    if (gap > 0) {
      blocks.push(
        <div
          key={`${trackId}-gap-${index}`}
          style={{ width: `${gap * pixelsPerMs}px` }}
        />
      );
      cursor += gap;
    }

    const width = Math.round(layout.duration * pixelsPerMs);
    cursor += layout.duration;

    blocks.push(
      <TimelineItemButton
        key={`${trackId}-${index}`}
        layout={layout}
        index={index}
        width={width}
        onSelectItem={onSelectItem}
      />
    );
  });

  return <div className="flex h-full items-stretch py-1.5">{blocks}</div>;
}

type TimelineItemButtonProps = {
  layout: PositionedItem<TimelineTrack["items"][number]>;
  index: number;
  width: number;
  onSelectItem?: (itemId: string) => void;
};

function TimelineItemButtonComponent({
  layout,
  index,
  width,
  onSelectItem,
}: TimelineItemButtonProps) {
  const { kind, id } = layout.item;
  const versionIds = "versionIds" in layout.item ? layout.item.versionIds : [];
  const { borderColor, height } = getTrackTheme(kind);

  const hasUnseenVersion = useMemo(() => {
    if (!versionIds) return false;
    return versionIds.some(
      (vId) => vId !== id && unseenVersionStore.isUnseen(vId)
    );
  }, [versionIds, id]);

  const content = (() => {
    switch (kind) {
      case "audio":
        return <AudioWaveformItem url={layout.item.asset.url} />;
      case "shot":
        return (
          <TextTimelineItem
            color={borderColor}
            text={`Shot ${layout.item.shotIndex + 1}`}
          />
        );
      case "lyrics":
        return <TextTimelineItem color={borderColor} text={layout.item.text} />;
      default: {
        const thumbnail = (() => {
          let url =
            kind === "image"
              ? layout.item.asset.thumbnail || layout.item.asset.url
              : layout.item.asset.url
              ? layout.item.asset.thumbnail
              : "";

          if (kind === "video" && url && "videoClipStartTime" in layout.item) {
            const start = layout.item.videoClipStartTime;
            if (start !== undefined) {
              url += `#t=${start}`;
            }
          }
          return url;
        })();

        return (
          <div
            className="h-full w-full overflow-hidden bg-repeat-x bg-center cursor-pointer"
            style={{
              backgroundImage: `url(${thumbnail})`,
              backgroundSize: "auto 100%",
            }}
            aria-label={`${kind} thumbnail`}
          />
        );
      }
    }
  })();

  return (
    <div className="pr-0.5 last:pr-0" style={{ width, height }}>
      <Button
        type="plain"
        className={classNames(
          "relative group bg-black rounded-sm  w-full h-full border overflow-hidden"
        )}
        style={{ borderColor }}
        onClick={(event) => {
          event.stopPropagation();
          if (layout.item.kind === "shot" || layout.item.kind === "lyrics")
            return;
          onSelectItem?.(layout.item.id);
        }}
      >
        {content}
        {"isGenerating" in layout.item && layout.item.isGenerating && (
          <div className={classNames(styles.skeletonShimmer, "rounded-sm")} />
        )}
        {(kind === "image" || kind === "video") && (
          <div className="absolute top-1 left-1 w-4 h-4 bg-[rgba(0,0,0,0.4)] text-white rounded-sm flex items-center justify-center font-medium text-[10px] z-20 backdrop-blur-xl">
            {index + 1}
          </div>
        )}
        {hasUnseenVersion && (
          <div className="absolute top-0.5 right-0.5 px-1 bg-primary text-white rounded-sm text-xs font-bold z-20 shadow-sm leading-tight flex items-center justify-center scale-90 origin-top-right truncate text-ellipsis">
            New
          </div>
        )}
      </Button>
    </div>
  );
}

type TextTimelineItemProps = {
  text: string;
  color: string;
};

function TextTimelineItemComponent({ text, color }: TextTimelineItemProps) {
  return (
    <div
      className="flex h-full w-full items-center justify-center px-2 text-xs overflow-hidden truncate"
      style={{ color }}
    >
      {text}
    </div>
  );
}

type AudioWaveformItemProps = {
  url: string;
};

function AudioWaveformItemComponent({ url }: AudioWaveformItemProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { isReady } = useWavesurfer({
    container: containerRef,
    height: 30,
    barWidth: 1.2,
    barGap: 1.5,
    barRadius: 1,
    waveColor: "#6766CC",
    progressColor: "#6766CC",
    interact: false,
    dragToSeek: false,
    cursorWidth: 0,
    url,
  });

  return (
    <div className="relative w-full h-full">
      {!isReady && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <LoadingOutlined className="text-[#6766CC] text-sm animate-spin" />
        </div>
      )}
      <div
        ref={containerRef}
        className={classNames(
          "w-full h-full overflow-hidden transition-opacity duration-300",
          isReady ? "opacity-100" : "opacity-0"
        )}
      />
    </div>
  );
}

const AudioWaveformItem = memo(AudioWaveformItemComponent);
const TextTimelineItem = memo(TextTimelineItemComponent);
const TimelineItemButton = observer(TimelineItemButtonComponent);
const SequentialItems = memo(SequentialItemsComponent);
export const TrackRow = memo(TrackRowComponent);
